<template>
  <!-- One observer wraps the plan header AND every service-plan row, because always saved together as one unit.
       A new plan renders standalone (outlined card + title); editing an existing
       plan renders inline inside its plan card (flat, no title). -->
  <validation-observer ref="form" v-slot="{ invalid }">
    <v-card
      :outlined="isNewPlan"
      :flat="!isNewPlan"
      :class="isNewPlan ? 'mb-4' : ''"
    >
      <v-card-title v-if="isNewPlan" color="primary">
        New Plan
        <v-spacer></v-spacer>
        <v-btn color="primary" icon @click="$emit('cancel')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <plan-fields
          :plan="planForm"
          :editMode="true"
          :isNewPlan="isNewPlan"
        ></plan-fields>

        <!-- Service plans -->
        <service-plans-table
          :servicePlans="servicePlans"
          :editMode="true"
          :plan="planForm"
          :agingOutDate="agingOutDate"
          @add="addServicePlan"
          @remove="removeServicePlan"
        ></service-plans-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn small color="primary" text @click="cancel">Cancel</v-btn>
        <v-btn
          small
          color="primary"
          :disabled="invalid || loading"
          @click="save"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </validation-observer>
</template>

<script>
import Plan from "../../../objs/Plan";
import ServicePlan from "../../../objs/ServicePlan";
import ServicePlansTable from "./service-plans-table";
import PlanFields from "./plan-fields";

export default {
  components: { ServicePlansTable, PlanFields },
  // A new plan (no `plan` passed) is the standalone editor; an existing plan is
  // the inline editor rendered inside its plan card. That distinction drives the
  // chrome and the Cancel/Save behavior below.
  props: {
    plan: { default: null },
  },
  data() {
    return {
      loading: false,
      planForm: new Plan(),
      servicePlans: [],
      // Temporary negative ids for newly-added rows so each row has a unique
      // servicePlanId key in the table; normalized to 0 (create) on save.
      tempId: 0,
    };
  },
  mounted() {
    this.resetForm();
  },
  computed: {
    isNewPlan() {
      return !Boolean(this.plan);
    },
    isIncomplete() {
      return Boolean(this.plan) && this.plan.isIncomplete;
    },
    client() {
      return this.$store.state.clientModule.selectedClient;
    },
    isPreschoolPlan() {
      return this.planForm.ageCategory === "Preschool";
    },
    // The client carries a backend-computed aging-out date for each plan length; pick the one for the
    // current 10/12-month selection. Reactive to the toggle, so no round-trip. Null when it doesn't
    // apply (not Preschool, or no 10/12 chosen). Backend AgingOutCalculator is the source of truth.
    agingOutDate() {
      var isSummer = this.planForm.isSummerSemesterIncluded;
      if (
        !this.client ||
        !this.isPreschoolPlan ||
        isSummer === null ||
        isSummer === undefined
      ) {
        return null;
      }
      var date = isSummer
        ? this.client.agingOutDateTwelveMonth
        : this.client.agingOutDateTenMonth;
      return date ? new Date(date) : null;
    },
  },
  methods: {
    resetForm() {
      if (this.isNewPlan) {
        this.planForm = new Plan();
        this.planForm.clientId = this.client.clientId;
        this.servicePlans = [];
      } else {
        this.planForm = new Plan(this.plan);
        // For an incomplete ("Unassigned") plan, clear the placeholder type so real plan type is required to complete the form
        if (this.isIncomplete) {
          this.planForm.planTypeId = null;
          this.planForm.planType = null;
        }
        this.planForm.planStartDate = this.plan.planStartDate
          ? new Date(this.plan.planStartDate)
          : null;
        this.planForm.planMeetingDate = this.plan.planMeetingDate
          ? new Date(this.plan.planMeetingDate)
          : null;
        this.planForm.closureDate = this.plan.closureDate
          ? new Date(this.plan.closureDate)
          : null;
        this.servicePlans = (this.plan.servicePlans || []).map((sp) =>
          this.resetServicePlanRow(sp)
        );
      }
    },
    // Build an editable local copy of a stored service plan (Inverse of toServicePlanPayload.)
    resetServicePlanRow(servicePlan) {
      return {
        ...new ServicePlan(servicePlan),
        planId: this.plan.planId,
        clientId: this.plan.clientId,
        startDate: servicePlan.startDate
          ? new Date(servicePlan.startDate)
          : null,
        endDate: servicePlan.endDate ? new Date(servicePlan.endDate) : null,
        isNew: false, // sort key: existing rows sort above newly-added ones
      };
    },
    addServicePlan() {
      var startDate = this.planForm.planStartDate
        ? new Date(this.planForm.planStartDate)
        : null;
      var defaultEndDate = startDate
        ? new Date(new Date(startDate).setFullYear(startDate.getFullYear() + 1))
        : null;
      // never default past the aging-out date - the backend rejects it, so avoid a default-then-error
      if (defaultEndDate && this.agingOutDate && defaultEndDate > this.agingOutDate) {
        defaultEndDate = new Date(this.agingOutDate);
      }
      this.servicePlans.push({
        ...new ServicePlan(),
        servicePlanId: --this.tempId,
        planId: this.isNewPlan ? null : this.plan.planId,
        clientId: this.planForm.clientId,
        startDate: startDate,
        endDate: defaultEndDate,
        isNew: true, // sort key: keeps newly-added rows at the bottom
      });
    },
    removeServicePlan(item) {
      var index = this.servicePlans.indexOf(item);
      if (index >= 0) {
        this.servicePlans.splice(index, 1);
      }
    },
    cancel() {
      // Existing plan (inline editor inside a plan card): Cancel discards unsaved
      // edits by reloading the form from the plan, and stays open — the parent's
      // "Plan Edit mode" toggle is what shows/hides it.
      // New plan (standalone editor): emit cancel so the parent tears it down.
      if (!this.isNewPlan) {
        this.resetForm();
      } else {
        this.$emit("cancel");
      }
    },
    // Convert an editable row to the API shape: strip the navigation and local properties
    // and set the id — a temporary (negative) id becomes 0, which tells the backend to create it.
    toServicePlanPayload({ assignments, transmittal, isNew, ...sp }) {
      return {
        ...sp,
        servicePlanId: sp.servicePlanId > 0 ? sp.servicePlanId : 0,
      };
    },
    async save() {
      this.loading = true;
      this.$store.commit("uxModule/setShowLoader", true);
      // header fields only (client omitted), plus cleaned service plans
      var { client, ...plan } = this.planForm;
      plan.servicePlans = this.servicePlans.map(this.toServicePlanPayload);
      var message = await this.$store.dispatch("plansModule/savePlan", plan);
      this.loading = false;
      this.$store.commit("uxModule/setShowLoader", false);
      if (message) {
        this.$store.commit("uxModule/setSnackbarMsg", message);
      } else {
        this.$store.commit(
          "uxModule/setSnackbarMsg",
          "Plan saved successfully"
        );
        if (!this.isNewPlan) {
          // existing plan: reload from the now-updated store record, stay open
          this.resetForm();
        } else {
          this.$emit("saved");
        }
      }
      this.$store.commit("uxModule/setShowSnackbar", true);
    },
  },
};
</script>
